import React, { useState, useEffect } from 'react';
import { clinicService } from '../../services/api';

const AppointmentReminderSettingsSection = ({ clinicId, onDataChange }) => {
  const [settings, setSettings] = useState([]);
  const [doctors, setDoctors] = useState([]);
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [editingSettingId, setEditingSettingId] = useState(null);
  const [filters, setFilters] = useState({
    location_id: '',
    doctor_id: '',
    enabled: null
  });

  const [formData, setFormData] = useState({
    timezone: 'Asia/Hong_Kong',
    location_id: '',
    doctor_id: '',
    enabled: false,
    reminder_days_before: 1,
    cutoff_reminder_at: '17:00'
  });

  // Common timezones
  const timezones = [
    'Asia/Hong_Kong',
    'Asia/Singapore',
    'Asia/Shanghai',
    'Asia/Tokyo',
    'UTC'
  ];

  useEffect(() => {
    fetchSettings();
    fetchDoctors();
    fetchLocations();
  }, [clinicId, filters]);

  const fetchSettings = async () => {
    try {
      setLoading(true);
      const response = await clinicService.getAppointmentReminderSettings(clinicId, filters);
      setSettings(response.items || []);
    } catch (err) {
      console.error('Error fetching appointment reminder settings:', err);
      setError('Failed to load appointment reminder settings');
    } finally {
      setLoading(false);
    }
  };

  const fetchDoctors = async () => {
    try {
      const response = await clinicService.getClinicDoctors(clinicId);
      setDoctors(response.doctors || []);
    } catch (err) {
      console.error('Error fetching doctors:', err);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await clinicService.getClinicLocations(clinicId);
      setLocations(response.locations || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
    }
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value === '' ? null : value
    }));
  };

  const handleAddNew = () => {
    setFormData({
      timezone: 'Asia/Hong_Kong',
      location_id: '',
      doctor_id: '',
      enabled: false,
      reminder_days_before: 1,
      cutoff_reminder_at: '17:00'
    });
    setEditingSettingId(null);
    setIsEditing(true);
    setError(null);
  };

  const handleEdit = (setting) => {
    setFormData({
      timezone: setting.timezone || 'Asia/Hong_Kong',
      location_id: setting.location_id ? String(setting.location_id) : '',
      doctor_id: setting.doctor_id ? String(setting.doctor_id) : '',
      enabled: setting.enabled || false,
      reminder_days_before: setting.reminder_days_before || 1,
      cutoff_reminder_at: setting.cutoff_reminder_at || '17:00'
    });
    setEditingSettingId(setting.id);
    setIsEditing(true);
    setError(null);
  };

  const handleCancel = () => {
    setIsEditing(false);
    setEditingSettingId(null);
    setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    // Validate cutoff_reminder_at format (HH:MM)
    const timeRegex = /^([0-1][0-9]|2[0-3]):[0-5][0-9]$/;
    if (!timeRegex.test(formData.cutoff_reminder_at)) {
      setError('Cutoff reminder time must be in HH:MM format (e.g., 17:00)');
      return;
    }

    try {
      setLoading(true);
      const submitData = {
        timezone: formData.timezone,
        location_id: formData.location_id || null,
        doctor_id: formData.doctor_id || null,
        enabled: formData.enabled,
        reminder_days_before: parseInt(formData.reminder_days_before),
        cutoff_reminder_at: formData.cutoff_reminder_at
      };

      if (editingSettingId) {
        await clinicService.updateAppointmentReminderSetting(clinicId, editingSettingId, submitData);
        setSuccessMessage('Appointment reminder setting updated successfully');
      } else {
        await clinicService.createAppointmentReminderSetting(clinicId, submitData);
        setSuccessMessage('Appointment reminder setting created successfully');
      }

      await fetchSettings();
      setIsEditing(false);
      setEditingSettingId(null);
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error saving appointment reminder setting:', err);
      setError(err.response?.data?.detail || 'Failed to save appointment reminder setting');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (settingId) => {
    if (!window.confirm('Are you sure you want to delete this appointment reminder setting?')) {
      return;
    }

    try {
      setLoading(true);
      await clinicService.deleteAppointmentReminderSetting(clinicId, settingId);
      setSuccessMessage('Appointment reminder setting deleted successfully');
      await fetchSettings();
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error deleting appointment reminder setting:', err);
      setError(err.response?.data?.detail || 'Failed to delete appointment reminder setting');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleEnabled = async (settingId) => {
    try {
      setLoading(true);
      await clinicService.toggleAppointmentReminderSettingEnabled(clinicId, settingId);
      setSuccessMessage('Appointment reminder setting status toggled successfully');
      await fetchSettings();
      if (onDataChange) onDataChange();
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Error toggling appointment reminder setting:', err);
      setError(err.response?.data?.detail || 'Failed to toggle appointment reminder setting');
    } finally {
      setLoading(false);
    }
  };

  const getDoctorName = (doctorId) => {
    if (!doctorId) return 'N/A';
    const doctor = doctors.find(d => d.id === doctorId);
    return doctor ? `${doctor.primary_name} (${doctor.speciality})` : doctorId;
  };

  const getLocationName = (locationId) => {
    if (!locationId) return 'N/A';
    const location = locations.find(l => l.id === locationId);
    if (!location) return locationId;
    
    // Try to get i18n name, fallback to primary_name
    // For dashboard, we'll try en_US first, then zh_HK, then zh_CN, then primary_name
    const i18nName = location.meta_data?.i18n?.name?.['en_US'] 
      || location.meta_data?.i18n?.name?.['zh_HK']
      || location.meta_data?.i18n?.name?.['zh_CN']
      || location.primary_name;
    return i18nName;
  };

  const getLocationDisplayName = (location) => {
    // Try to get i18n name, fallback to primary_name
    // For dashboard, we'll try en_US first, then zh_HK, then zh_CN, then primary_name
    const i18nName = location.meta_data?.i18n?.name?.['en_US'] 
      || location.meta_data?.i18n?.name?.['zh_HK']
      || location.meta_data?.i18n?.name?.['zh_CN']
      || location.primary_name;
    return i18nName;
  };

  return (
    <div className="bg-white p-6 rounded-lg shadow-md">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-700">Appointment Reminder Settings</h3>
        {!isEditing && (
          <button
            onClick={handleAddNew}
            className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 flex items-center"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            Add New Setting
          </button>
        )}
      </div>

      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}

      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      {/* Filters */}
      {!isEditing && (
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Filters</h4>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Location
              </label>
              <select
                name="location_id"
                value={filters.location_id || ''}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded"
              >
                <option value="">All Locations</option>
                {locations.map(location => (
                  <option key={location.id} value={String(location.id)}>
                    {getLocationDisplayName(location)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Doctor
              </label>
              <select
                name="doctor_id"
                value={filters.doctor_id || ''}
                onChange={handleFilterChange}
                className="w-full p-2 border rounded"
              >
                <option value="">All Doctors</option>
                {doctors.map(doctor => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.primary_name} - {doctor.speciality}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Enabled Status
              </label>
              <select
                name="enabled"
                value={filters.enabled === null ? '' : filters.enabled.toString()}
                onChange={(e) => setFilters(prev => ({
                  ...prev,
                  enabled: e.target.value === '' ? null : e.target.value === 'true'
                }))}
                className="w-full p-2 border rounded"
              >
                <option value="">All</option>
                <option value="true">Enabled</option>
                <option value="false">Disabled</option>
              </select>
            </div>
            <div className="flex items-end">
              <button
                onClick={() => setFilters({ location_id: '', doctor_id: '', enabled: null })}
                className="w-full px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Clear Filters
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Form */}
      {isEditing && (
        <div className="mb-6 p-6 border rounded-lg bg-gray-50">
          <h4 className="text-lg font-medium mb-4">
            {editingSettingId ? 'Edit Setting' : 'Add New Setting'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Timezone <span className="text-red-500">*</span>
                </label>
                <select
                  name="timezone"
                  value={formData.timezone}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                >
                  {timezones.map(tz => (
                    <option key={tz} value={tz}>{tz}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Location
                </label>
                <select
                  name="location_id"
                  value={formData.location_id}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="">All Locations (No specific location)</option>
                  {locations.map(location => (
                    <option key={location.id} value={String(location.id)}>
                      {getLocationDisplayName(location)}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Doctor
                </label>
                <select
                  name="doctor_id"
                  value={formData.doctor_id}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                >
                  <option value="">All Doctors (No specific doctor)</option>
                  {doctors.map(doctor => (
                    <option key={doctor.id} value={doctor.id}>
                      {doctor.primary_name} - {doctor.speciality}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Reminder Days Before <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  name="reminder_days_before"
                  value={formData.reminder_days_before}
                  onChange={handleInputChange}
                  min="1"
                  max="30"
                  className="w-full p-2 border rounded"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Number of days before appointment to send reminder</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cutoff Reminder At <span className="text-red-500">*</span>
                </label>
                <input
                  type="time"
                  name="cutoff_reminder_at"
                  value={formData.cutoff_reminder_at}
                  onChange={handleInputChange}
                  className="w-full p-2 border rounded"
                  required
                />
                <p className="text-xs text-gray-500 mt-1">Time cutoff for sending reminders (HH:MM format)</p>
              </div>
              <div className="flex items-center">
                <input
                  type="checkbox"
                  name="enabled"
                  checked={formData.enabled}
                  onChange={handleInputChange}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                <label className="ml-2 text-sm font-medium text-gray-700">
                  Enabled
                </label>
              </div>
            </div>
            <div className="flex justify-end space-x-3">
              <button
                type="button"
                onClick={handleCancel}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={loading}
                className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 disabled:opacity-50"
              >
                {loading ? 'Saving...' : (editingSettingId ? 'Update' : 'Create')}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Settings List */}
      {!isEditing && (
        <div>
          {loading ? (
            <p className="text-gray-500">Loading settings...</p>
          ) : settings.length === 0 ? (
            <p className="text-gray-500">No appointment reminder settings found.</p>
          ) : (
            <div className="space-y-4">
              {settings.map(setting => (
                <div key={setting.id} className="border rounded-lg p-4 bg-white">
                  <div className="flex justify-between items-start mb-3">
                    <div>
                      <h5 className="font-medium text-gray-700">
                        Setting {setting.id.substring(0, 8)}
                        {setting.enabled && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-green-100 text-green-800 rounded-full">Enabled</span>
                        )}
                        {!setting.enabled && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-gray-100 text-gray-800 rounded-full">Disabled</span>
                        )}
                      </h5>
                    </div>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleToggleEnabled(setting.id)}
                        className={`px-3 py-1 text-xs rounded ${
                          setting.enabled
                            ? 'bg-yellow-500 text-white hover:bg-yellow-600'
                            : 'bg-green-500 text-white hover:bg-green-600'
                        }`}
                      >
                        {setting.enabled ? 'Disable' : 'Enable'}
                      </button>
                      <button
                        onClick={() => handleEdit(setting)}
                        className="px-3 py-1 text-xs bg-blue-500 text-white rounded hover:bg-blue-600"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(setting.id)}
                        className="px-3 py-1 text-xs bg-red-500 text-white rounded hover:bg-red-600"
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-sm">
                    <div>
                      <span className="text-gray-500">Timezone:</span>
                      <span className="ml-2 font-medium">{setting.timezone}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Location:</span>
                      <span className="ml-2 font-medium">{getLocationName(setting.location_id)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Doctor:</span>
                      <span className="ml-2 font-medium">{getDoctorName(setting.doctor_id)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Days Before:</span>
                      <span className="ml-2 font-medium">{setting.reminder_days_before}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Cutoff Time:</span>
                      <span className="ml-2 font-medium">{setting.cutoff_reminder_at}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AppointmentReminderSettingsSection;

